import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Route, Router } from '@angular/router';
import { PermissionService } from './permission.service';

/**
 * Downloads the lazy feature bundles *before* the user clicks them.
 *
 * Every sidebar entry is a lazy route, and on this connection one first visit
 * costs 1.5–3s of chunk downloading (the app ships ~13 MB of chunks, several of
 * which are needed before a route can render). Once a chunk has been fetched the
 * same session, the route opens in ~5ms — so the click itself is not slow, the
 * missing bundle is. Prefetching after the first screen has settled turns those
 * first clicks into the same ~5ms.
 *
 * Deliberately NOT `PreloadAllModules`: that fires all ~29 module imports at once,
 * which splits the bandwidth across every chunk and makes all of them slow. Here
 * they are fetched one at a time, in sidebar order — the pages an admin opens
 * first are ready first — and `prioritize()` lets a hovered/clicked link jump
 * the queue.
 *
 * The loaders come from the router config itself (the same `loadChildren`
 * functions), so this never drifts from the real routes.
 */
@Injectable({ providedIn: 'root' })
export class RoutePrefetchService {
  /** Sidebar order — matches layout.component.html top-to-bottom. */
  private static readonly PRIORITY = [
    'dashboard',
    'ai-insights',
    'contests',
    'quiz',
    'task-management',
    'categories',
    'users',
    'clients',
    'client-qr',
    'branding',
    'notification',
    'newsletter',
    'feedback',
    'request',
    'email-templates',
    'analysis-reports',
    'setting',
    'subscription',
    'terms-condition',
    'ads',
    'sectors',
    'interests',
    'business-objective',
    'activity-logs',
    'loyalty',
    'referral-config',
    'super-admin-logs',
    'profile',
  ];

  private queue: PrefetchEntry[] = [];
  private started = false;
  private running = false;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private router: Router,
    private permissions: PermissionService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  /**
   * Begin prefetching in the background. The delay keeps the first screen's own
   * API calls (and its images) ahead of the background downloads.
   */
  start(delayMs = 1500): void {
    if (!isPlatformBrowser(this.platformId) || this.started) return;

    // Data-saver / very slow connections: the user asked for less traffic, so
    // don't spend their bytes on pages they may never open.
    if ((navigator as any).connection?.saveData) return;

    this.started = true;
    this.timer = setTimeout(() => {
      this.timer = null;
      this.pump();
    }, delayMs);
  }

  /**
   * A sidebar link is hovered or clicked — fetch its bundle next, ahead of
   * whatever is queued behind it. Cheap to call repeatedly: a link whose module
   * is already loaded (or in flight) is ignored.
   */
  prioritize(url: string): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const entry = this.entryFor(url);
    if (!entry || entry.state !== 'pending') return;

    const queued = this.queue.indexOf(entry);
    if (queued > 0) this.queue.splice(queued, 1);
    if (queued !== 0) this.queue.unshift(entry);

    // Hovering means the user is about to need it — don't wait for the idle tick.
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.pump();
  }

  /** Resolve a route url ('/contests/add?x=1') to its queued entry. */
  private entryFor(url: string): PrefetchEntry | undefined {
    const path = url.split('?')[0].replace(/^\/+/, '').split('/')[0];
    if (!path) return undefined;
    return this.entries().find((e) => e.path === path);
  }

  /** Build the queue from the router's own lazy routes, sidebar-first. */
  private entries(): PrefetchEntry[] {
    if (this.queue.length) return this.queue;

    const found: PrefetchEntry[] = [];
    const collect = (routes: Route[], parent: string, inheritedData?: any) => {
      for (const route of routes) {
        const data = route.data ?? inheritedData;

        if (typeof route.loadChildren === 'function') {
          const path = parent || route.path || '';
          if (path && path !== 'auth' && !path.includes('*') && this.mayPrefetch(data)) {
            found.push({
              path,
              load: route.loadChildren as unknown as () => Promise<unknown>,
              state: 'pending',
            });
          }
          continue;
        }
        if (route.children?.length) {
          collect(route.children, parent || route.path || '', data);
        }
      }
    };
    collect(this.router.config, '');

    const rank = (path: string) => {
      const i = RoutePrefetchService.PRIORITY.indexOf(path);
      return i === -1 ? RoutePrefetchService.PRIORITY.length : i;
    };
    found.sort((a, b) => rank(a.path) - rank(b.path));

    this.queue = found;
    return this.queue;
  }

  /**
   * Don't spend a sub-admin's bandwidth on pages their account can't open. Any
   * doubt (no pageKey on the route, or the check itself failing) means "yes" —
   * a missed prefetch only costs the old wait, a wrongly skipped one is invisible.
   */
  private mayPrefetch(data: any): boolean {
    const pageKey = data?.['pageKey'];
    if (!pageKey) return true;
    try {
      return this.permissions.canView(pageKey);
    } catch {
      return true;
    }
  }

  /** Fetch the next pending bundle, then schedule the one after it. */
  private pump(): void {
    if (this.running || !isPlatformBrowser(this.platformId)) return;

    const next = this.entries().find((e) => e.state === 'pending');
    if (!next) return;

    next.state = 'loading';
    this.running = true;

    Promise.resolve()
      .then(() => next.load())
      .then(() => {
        next.state = 'done';
      })
      .catch(() => {
        // A failed prefetch must never break the app — mark it done and move on;
        // a real navigation will surface the error properly.
        next.state = 'done';
      })
      .then(() => {
        this.running = false;
        this.scheduleNext();
      });
  }

  private scheduleNext(): void {
    if (typeof window === 'undefined') return;
    const idle = (window as any).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout: number }) => number)
      | undefined;

    if (idle) {
      idle(() => this.pump(), { timeout: 2000 });
      return;
    }
    this.timer = setTimeout(() => {
      this.timer = null;
      this.pump();
    }, 200);
  }
}

interface PrefetchEntry {
  path: string;
  load: () => Promise<unknown>;
  state: 'pending' | 'loading' | 'done';
}
